import { Controller, useFormContext } from 'react-hook-form';
import { TextArea, TextInput } from '~/components';
import { FormFeedback } from '~/components/forms/FormFeedback';
import { FormField } from '~/components/forms/FormField';
import { getFieldError } from '~/components/forms/formUtils';
import { createFormFieldStyles } from '~/constants/styles';
import { LABOR_REQ_TYPE_OPTIONS } from '~/features/market/listings/msaas-listing/wizard-steps/labor-metrics/constants';
import {
  htmlToText,
  SERVICE_TERM_TABS,
  serviceTermLabel,
  type ServiceTerm,
} from '~/features/market/negotiations/termDisplay';
import {
  CallShiftDifferentialType,
  CompensationType,
  RVUPeriod,
  ShiftDifferentialType,
} from '~/types/hlx/enums';
import type { ServiceOffer } from './schemas';
import { isServiceCounterIntegerTerm, type ServiceCounterDraft } from './serviceCounterDraft';

// The term groups and labels live in a component-free module so the negotiation
// history can share them; re-exported for the counteroffer editor.
export { SERVICE_TERM_TABS, serviceTermLabel, type ServiceTerm };
export const AVAILABILITY_TAB = 'Availability';
export const SERVICE_NEGOTIATION_TAB_NAMES = [
  ...SERVICE_TERM_TABS.map((tab) => tab.name),
  AVAILABILITY_TAB,
];

/** Index of the tab holding a form field, or -1 for fields outside the tabs. */
export const tabIndexForField = (name: string) => {
  if (name === 'windowStart' || name === 'windowEnd')
    return SERVICE_NEGOTIATION_TAB_NAMES.indexOf(AVAILABILITY_TAB);
  return SERVICE_TERM_TABS.findIndex((tab) =>
    tab.sections.some((section) => section.terms.some((term) => term === name))
  );
};

const OPTIONS: Partial<Record<ServiceTerm, readonly string[]>> = {
  compType: Object.values(CompensationType),
  laborReqType: LABOR_REQ_TYPE_OPTIONS.map((option) => option.value),
  shiftDiffType: Object.values(ShiftDifferentialType),
  callShiftDiffType: Object.values(CallShiftDifferentialType),
  rvuPeriod: Object.values(RVUPeriod),
  rvuBonusPeriod: ['HOUR', 'SHIFT', 'WEEK', 'YEAR'],
};
export const isRequiredTerm = (term: ServiceTerm) => term === 'laborReqType' || term === 'compType';

/** The value as the form stores it back into the counter draft. */
export const termValue = (key: ServiceTerm, value: string) =>
  value === '' ? null : isServiceCounterIntegerTerm(key) ? Number(value) : value;

export const termChanged = (
  term: ServiceTerm,
  proposed: Partial<ServiceCounterDraft['terms']>,
  previous: ServiceOffer['terms']
) => String(proposed[term] ?? '') !== String(previous[term] ?? '');

/** The previous offer's value as the comparison column prints it. */
export const previousTermText = (term: ServiceTerm, value: string | number | null | undefined) => {
  if (typeof value !== 'string') return value;
  if (term.endsWith('Txt')) return htmlToText(value);
  return OPTIONS[term] ? value.replace(/_/g, ' ') : value;
};

/**
 * One proposed-term input. The previous value beside it carries the visible
 * label, so this one is visually hidden but still names the control. The
 * wrapper is `relative` so the hidden label stays inside the modal's scroller
 * instead of stretching it (the same reason FormLabel is `relative`).
 */
export const TermField = ({ term, flagged }: { term: ServiceTerm; flagged: boolean }) => {
  const { control, formState } = useFormContext();
  const label = serviceTermLabel(term);
  const choices = OPTIONS[term];
  const required = isRequiredTerm(term);
  return (
    <div className="relative w-full [&_label]:sr-only">
      {choices ? (
        <Controller
          name={term}
          control={control}
          render={({ field }) => {
            const fieldError = getFieldError(formState.errors, term);
            return (
              <FormField
                id={term}
                label={label}
                required={required}
                feedback={<FormFeedback error={fieldError?.message} errorId={`${term}-error`} />}
              >
                <select
                  {...field}
                  id={term}
                  className={createFormFieldStyles(flagged, false, !!fieldError).join(' ')}
                  aria-required={required || undefined}
                  aria-invalid={!!fieldError || undefined}
                  aria-describedby={fieldError ? `${term}-error` : undefined}
                >
                  <option value="">Not specified</option>
                  {choices.map((value) => (
                    <option key={value} value={value}>
                      {value.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </FormField>
            );
          }}
        />
      ) : term.endsWith('Txt') ? (
        <TextArea name={term} label={label} rows={3} flagged={flagged} className="min-w-56" />
      ) : (
        <TextInput
          name={term}
          label={label}
          type={term.endsWith('Date') ? 'date' : 'number'}
          flagged={flagged}
        />
      )}
    </div>
  );
};
